/**
 * agentConfig.js — agent endpoints from the Vite env (CONTRACT §10.1). Never imported by tests.
 *
 * The dev values live in vehicle-doctor-poc/.env.development (development mode only). Production
 * builds see no VITE_AGENT_* key, so AGENT_ENABLED is false and useAgent never connects.
 */

import { makeClientId, isValidClientId, resolveAgentUrl } from './agentProtocol.js';

export const AGENT_WS_ENDPOINT = import.meta.env.VITE_AGENT_WS_ENDPOINT || '';   // '' = agent features off
export const AGENT_BASE_URL = import.meta.env.VITE_AGENT_BASE_URL || '';
export const AGENT_ENABLED = !!AGENT_WS_ENDPOINT;

const loc = () => (typeof window !== 'undefined' ? window.location : null);

/** Absolute ws(s):// URL of the agent socket ('' when agent features are off). */
export function agentWsUrl() {
  return resolveAgentUrl(AGENT_WS_ENDPOINT, loc(), { ws: true });
}

/** Absolute http(s) base for REST and file URLs, without a trailing slash. */
export function agentBaseUrl() {
  return resolveAgentUrl(AGENT_BASE_URL, loc());
}

const CLIENT_ID_KEY = 'mhi_agent_client_id';

/**
 * The per-tab client id, shared by the agent socket (hello) and voice (session.start.client_id).
 * Kept in sessionStorage; a fresh id when storage is unavailable.
 */
let cachedClientId = null;

export function getAgentClientId() {
  if (cachedClientId) return cachedClientId;
  const uuid = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : '');
  try {
    const stored = window.sessionStorage.getItem(CLIENT_ID_KEY);
    if (isValidClientId(stored)) {
      cachedClientId = stored;
    } else {
      cachedClientId = makeClientId(uuid);
      window.sessionStorage.setItem(CLIENT_ID_KEY, cachedClientId);
    }
  } catch {
    // Storage blocked: one id for the life of this page, so agent and voice still agree.
    cachedClientId = cachedClientId || makeClientId(uuid);
  }
  return cachedClientId;
}
